import { 
  query, 
  where, 
  getDocs, 
  updateDoc,
  addDoc,
  orderBy,
  limit,
  startAfter,
  Timestamp,
  DocumentData,
  QueryDocumentSnapshot
} from "firebase/firestore";
import { db, referralsCollection, initializeFirebase } from "./init";
import { executeWithRetry, ensureValidAuth } from "./utils";
import { ReferralData } from "./types";
import { checkOnlineStatus } from "./init";

export const addReferral = async (referralData: ReferralData): Promise<string> => {
  return executeWithRetry(async () => {
    const docRef = await addDoc(referralsCollection, {
      ...referralData,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    });
    return docRef.id;
  });
};

export const getReferralsByCode = async (referrerCode: string, lastDoc: any = null, pageSize = 10): Promise<{ referrals: ReferralData[], lastVisible: any }> => {
  return executeWithRetry(async () => {
    try {
      await initializeFirebase();
      
      let referralsQuery = query(
        referralsCollection,
        where("referrerCode", "==", referrerCode),
        orderBy("createdAt", "desc"),
        limit(pageSize)
      );
      
      if (lastDoc) {
        referralsQuery = query(
          referralsCollection,
          where("referrerCode", "==", referrerCode),
          orderBy("createdAt", "desc"),
          startAfter(lastDoc),
          limit(pageSize)
        );
      }
      
      const querySnapshot = await getDocs(referralsQuery);
      const lastVisible = querySnapshot.docs[querySnapshot.docs.length - 1];
      
      const referrals = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as ReferralData[];
      
      return { referrals, lastVisible };
    } catch (error) {
      console.error("Error getting referrals:", error);
      throw error;
    }
  });
};

export const getAllReferrals = async (lastDoc: any = null, pageSize = 20): Promise<{ referrals: ReferralData[], lastVisible: any }> => {
  return executeWithRetry(async () => {
    try {
      await ensureValidAuth();
      
      if (!db) {
        await initializeFirebase();
      }
      
      let referralsQuery = query(
        referralsCollection,
        orderBy("createdAt", "desc"),
        limit(pageSize)
      );
      
      if (lastDoc) {
        referralsQuery = query(
          referralsCollection,
          orderBy("createdAt", "desc"),
          startAfter(lastDoc),
          limit(pageSize)
        );
      }
      
      const querySnapshot = await getDocs(referralsQuery);
      const lastVisible = querySnapshot.docs[querySnapshot.docs.length - 1];
      
      const referrals = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as ReferralData[];
      
      return { referrals, lastVisible };
    } catch (error) {
      console.error("Error getting all referrals:", error);
      throw error;
    }
  });
};

export const updateReferralStatus = async (referralId: string, status: string): Promise<void> => {
  return executeWithRetry(async () => {
    const referralDocRef = doc(db, "referrals", referralId);
    await updateDoc(referralDocRef, {
      status,
      updatedAt: Timestamp.now()
    });
  });
};

export const getUserStats = async (referrerCode: string): Promise<any> => {
  return executeWithRetry(async () => {
    try {
      if (!db) {
        await initializeFirebase();
      }
      
      const referralsQuery = query(
        referralsCollection,
        where("referrerCode", "==", referrerCode)
      );
      
      const soldQuery = query(
        referralsCollection,
        where("referrerCode", "==", referrerCode),
        where("status", "==", "sold")
      );
      
      const [referralsSnapshot, soldSnapshot] = await Promise.all([
        getDocs(referralsQuery),
        getDocs(soldQuery)
      ]);
      
      const totalReferrals = referralsSnapshot.size;
      const soldReferrals = soldSnapshot.size;
      
      let totalCommission = 0;
      soldSnapshot.forEach((doc: QueryDocumentSnapshot<DocumentData>) => {
        const data = doc.data();
        if (data.commission) {
          totalCommission += data.commission;
        }
      });
      
      // Calculate stats by month for the chart
      const monthlyStats: { [key: string]: { total: number, sold: number } } = {};
      referralsSnapshot.forEach((doc: QueryDocumentSnapshot<DocumentData>) => {
        const data = doc.data();
        if (data.createdAt) {
          const createdAtTimestamp = data.createdAt as Timestamp;
          const date = new Date(createdAtTimestamp.toDate());
          const monthYear = `${date.getMonth() + 1}/${date.getFullYear()}`;
          
          if (!monthlyStats[monthYear]) {
            monthlyStats[monthYear] = { total: 0, sold: 0 };
          }
          
          monthlyStats[monthYear].total += 1;
          
          if (data.status === 'sold') {
            monthlyStats[monthYear].sold += 1;
          }
        }
      });
      
      // Get achievements
      const achievements = [];
      
      if (totalReferrals >= 1) achievements.push({ id: 'first', title: 'First Referral', icon: '🌱' });
      if (totalReferrals >= 5) achievements.push({ id: 'five', title: '5 Referrals', icon: '🌿' });
      if (totalReferrals >= 10) achievements.push({ id: 'ten', title: '10 Referrals', icon: '🌳' });
      if (totalReferrals >= 25) achievements.push({ id: 'twentyfive', title: 'Referral Pro', icon: '🏆' });
      if (totalReferrals >= 50) achievements.push({ id: 'fifty', title: 'Referral Master', icon: '👑' });
      if (soldReferrals >= 1) achievements.push({ id: 'firstsale', title: 'First Sale', icon: '💰' });
      if (soldReferrals >= 5) achievements.push({ id: 'fivesales', title: '5 Sales', icon: '💎' });
      if (soldReferrals >= 10) achievements.push({ id: 'tensales', title: 'Sales Expert', icon: '🚀' });
      
      return {
        totalReferrals,
        soldReferrals,
        totalCommission,
        conversionRate: totalReferrals > 0 ? (soldReferrals / totalReferrals) * 100 : 0,
        monthlyStats,
        achievements
      };
    } catch (error) {
      console.error("Error getting user stats:", error);
      throw error;
    }
  });
};
